import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { logger } from "../../../utils/logs/core";
import { LOGS } from "../../../utils/logs/logConfig";
import { useEventBus } from "../../events/hooks/use-events-context";
import { NetworkProviderContext } from "../hooks/use-network-context";
import {
    NetworkContextValue,
    NetworkProviderConfig,
    NetworkStatus,
} from "../types/network";

/**
 * Au-delà de ce multiple de l'intervalle de sondage, l'écart entre deux vérifications ne
 * s'explique plus par un bridage de minuteurs : le poste a dormi, le décompte repart de zéro.
 * Le bridage le plus agressif vaut six intervalles ; la borne est posée au-dessus pour
 * absorber la dérive d'ordonnancement.
 */
const GAP_TOLERANCE_FACTOR = 9;

type NetworkProviderProps = PropsWithChildren<{
    config?: NetworkProviderConfig;
}>;

export function NetworkProvider({ children, config }: NetworkProviderProps) {
    const { emit } = useEventBus();

    // States React
    const [status, setStatus] = useState<NetworkStatus>("checking");
    const [isOnline, setIsOnline] = useState<boolean | null>(null);
    const [lastOnlineAt, setLastOnlineAt] = useState<number | null>(null);
    const [offlineSince, setOfflineSince] = useState<number | null>(null);

    // Refs pour les timers (pas de re-render)
    const pollingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
        null,
    );
    const thresholdReachedRef = useRef<boolean>(false);
    const previousStatusRef = useRef<boolean | null>(null);

    const observedOfflineMsRef = useRef<number>(0);

    const lastOfflineCheckAtRef = useRef<number | null>(null);

    const offlineStartedAtRef = useRef<number | null>(null);

    // Config avec valeurs par défaut
    const resolvedConfig = useMemo(
        () => ({
            reachabilityUrl:
                config?.reachabilityUrl ??
                "https://clients3.google.com/generate_204",
            pollingIntervalMs: config?.pollingIntervalMs ?? 10_000,
            pingTimeoutMs: config?.pingTimeoutMs ?? 3_000,
            offlineThresholdMs: config?.offlineThresholdMs ?? 20_000,
        }),
        [
            config?.reachabilityUrl,
            config?.pollingIntervalMs,
            config?.pingTimeoutMs,
            config?.offlineThresholdMs,
        ],
    );

    // --- Fonction de ping réel ---
    const checkConnectivity = useCallback(async (): Promise<boolean> => {
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(
                () => controller.abort(),
                resolvedConfig.pingTimeoutMs,
            );

            await fetch(resolvedConfig.reachabilityUrl, {
                method: "HEAD",
                mode: "no-cors",
                cache: "no-store",
                signal: controller.signal,
            });

            clearTimeout(timeoutId);
            return true;
        } catch {
            return false;
        }
    }, [resolvedConfig.reachabilityUrl, resolvedConfig.pingTimeoutMs]);

    /**
     * Cumule le temps hors ligne observé, et émet le seuil quand il est atteint.
     *
     * Un `setTimeout` mesurerait de l'horloge murale, qui avance pendant que le poste dort :
     * au réveil il concluait à une panne 4 s avant le retour du réseau (AB#1931). D'où le
     * comptage par intervalles, plafonné à un intervalle de sondage pour qu'un onglet bridé
     * progresse sans compter une minute de panne non observée.
     */
    const accumulateOfflineTime = useCallback(
        (now: number) => {
            const lastCheckAt = lastOfflineCheckAtRef.current;
            // Une horloge qui recule (NTP) donnerait un pas négatif.
            const step =
                lastCheckAt === null ? 0 : Math.max(0, now - lastCheckAt);
            const isSuspiciousGap =
                step > resolvedConfig.pollingIntervalMs * GAP_TOLERANCE_FACTOR;

            if (lastCheckAt === null || isSuspiciousGap) {
                if (isSuspiciousGap) {
                    logger.info(
                        LOGS.NetworkProvider,
                        `Offline countdown restarted after a ${step}ms gap (device suspended)`,
                    );
                }
                observedOfflineMsRef.current = 0;
                offlineStartedAtRef.current = now;
            } else {
                observedOfflineMsRef.current += Math.min(
                    step,
                    resolvedConfig.pollingIntervalMs,
                );
            }

            lastOfflineCheckAtRef.current = now;

            if (
                thresholdReachedRef.current ||
                observedOfflineMsRef.current < resolvedConfig.offlineThresholdMs
            ) {
                return;
            }

            thresholdReachedRef.current = true;
            logger.warn(
                LOGS.NetworkProvider,
                `Network offline for more than ${resolvedConfig.offlineThresholdMs}ms`,
            );
            emit("network-offline-threshold-reached", {
                // Pas `now - observed` : le temps observé est plafonné.
                offlineSince: offlineStartedAtRef.current ?? now,
                durationMs: observedOfflineMsRef.current,
            });
        },
        [
            emit,
            resolvedConfig.offlineThresholdMs,
            resolvedConfig.pollingIntervalMs,
        ],
    );

    // --- handleOnline ---
    const handleOnline = useCallback(() => {
        const now = Date.now();
        setStatus("online");
        setIsOnline(true);
        setLastOnlineAt(now);
        setOfflineSince(null);

        observedOfflineMsRef.current = 0;
        lastOfflineCheckAtRef.current = null;
        offlineStartedAtRef.current = null;
        thresholdReachedRef.current = false;

        // Émet 'network-online' si changement (inclut init null → true et transition false → true)
        if (previousStatusRef.current !== true) {
            logger.info(LOGS.NetworkProvider, "Network online");
            emit("network-online");
        }

        previousStatusRef.current = true;
    }, [emit]);

    // --- handleOffline ---
    const handleOffline = useCallback(() => {
        const now = Date.now();
        setStatus("offline");
        setIsOnline(false);

        // Émet 'network-offline' uniquement si transition
        if (previousStatusRef.current !== false) {
            logger.info(LOGS.NetworkProvider, "Network went offline");
            setOfflineSince(now);
            emit("network-offline");
        }

        accumulateOfflineTime(now);
        previousStatusRef.current = false;
    }, [accumulateOfflineTime, emit]);

    // --- Logique de mise à jour du statut ---
    const updateNetworkStatus = useCallback(async () => {
        // 1. Check rapide navigator.onLine
        if (!navigator.onLine) {
            handleOffline();
            return;
        }

        // 2. navigator.onLine === true → vérifier avec un ping réel
        setStatus("checking");
        const reachable = await checkConnectivity();

        if (reachable) {
            handleOnline();
        } else {
            handleOffline();
        }
    }, [checkConnectivity, handleOnline, handleOffline]);

    // --- useEffect principal ---
    useEffect(() => {
        logger.info(LOGS.NetworkProvider, "NetworkProvider initialized");

        // 1. Vérification initiale
        updateNetworkStatus();

        // 2. Écoute window events
        const onOnline = () => {
            logger.info(
                LOGS.NetworkProvider,
                'Browser "online" event received',
            );
            updateNetworkStatus();
        };
        const onOffline = () => {
            logger.info(
                LOGS.NetworkProvider,
                'Browser "offline" event received',
            );
            handleOffline();
        };

        window.addEventListener("online", onOnline);
        window.addEventListener("offline", onOffline);

        // 3. Polling de secours
        pollingIntervalRef.current = setInterval(() => {
            updateNetworkStatus();
        }, resolvedConfig.pollingIntervalMs);

        // 4. Cleanup
        return () => {
            window.removeEventListener("online", onOnline);
            window.removeEventListener("offline", onOffline);

            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
                pollingIntervalRef.current = null;
            }
        };
    }, [updateNetworkStatus, handleOffline, resolvedConfig.pollingIntervalMs]);

    // --- Context value (mémoïsée) ---
    const contextValue = useMemo<NetworkContextValue>(
        () => ({
            status,
            isOnline,
            lastOnlineAt,
            offlineSince,
        }),
        [status, isOnline, lastOnlineAt, offlineSince],
    );

    return (
        <NetworkProviderContext.Provider value={contextValue}>
            {children}
        </NetworkProviderContext.Provider>
    );
}
